import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Home from "../pages/Home";
import { useAuth } from "../hooks/useAuth";
import NotFound from "../pages/NotFound";
import {
  LoginBox,
  getCustomAuthTokens,
  hasCustomAuth,
  clearCustomAuthTokens,
} from "../features/auth/LoginBox";
import Relay from "../features/auth/Relax";
import Logout from "../pages/Logout";
import RemoveAuthCodeFromUrl from "../components/RemoveAuthCodeFromUrl";
import SsoTest from "../pages/SsoTest";
import Loader from "../components/loader/Loader";
import { useSessionValidator } from "../hooks/useSessionValidator";
import {
  listenForCrossAppLogout,
  listenForCrossAppLogin,
} from "../utils/authBroadcast";
import { emitSessionExpired } from "../utils/sessionEvents";

const CUSTOM_AUTH_KEY = "datasurfr_custom_auth";

const AppRouter: React.FC = () => {
  const auth = useAuth();
  useSessionValidator();

  // Force re-render when a LOGIN broadcast is received from another datasurfr tab
  const [, setAuthVersion] = useState(0);

  // [AUTH] listen for login events broadcast by other datasurfr tabs
  useEffect(() => {
    const cleanup = listenForCrossAppLogin((source, tokens) => {
      if (source !== "datasurfr") return; // cross-app logins can't share datasurfr tokens
      if (hasCustomAuth()) return; // already authenticated — nothing to do
      if (!tokens) return;

      console.log(
        "[AUTH][datasurfr][AppRouter.tsx] LOGIN received from tab | source:",
        source,
        "— restoring session",
      );
      try {
        sessionStorage.setItem(CUSTOM_AUTH_KEY, JSON.stringify(tokens));
        setAuthVersion((v) => v + 1); // trigger re-render → getCustomAuthTokens() picks up stored tokens
      } catch {
        /* sessionStorage unavailable */
      }
    });
    return cleanup;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // [AUTH] listen for logout events broadcast by gosafe-frontend
  useEffect(() => {
    const cleanup = listenForCrossAppLogout((source) => {
      console.warn(
        "[AUTH][datasurfr][AppRouter.tsx] Cross-app logout received | source:",
        source,
        "— clearing local session and showing logout modal",
      );
      clearCustomAuthTokens();
      localStorage.clear();
      sessionStorage.clear();
      auth.removeUser();
      // Show the "logged out" modal instead of hard-redirecting — user clicks Sign In to proceed
      emitSessionExpired("logout");
    });
    return cleanup;
  }, [auth]);

  // Dev bypass: skip all auth checks and render Home directly
  if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === "true") {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/home" element={<Home accessInfo={{ access_token: "dev", refresh_token: "dev" }} />} />
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Check for custom auth tokens first (custom login)
  const customTokens = getCustomAuthTokens();
  const isCustomAuthenticated = hasCustomAuth();

  // If user has custom auth tokens, they're authenticated
  if (isCustomAuthenticated && customTokens) {
    return (
      <BrowserRouter>
        <RemoveAuthCodeFromUrl />
        <Routes>
          <Route
            path="/home"
            element={
              <Home
                accessInfo={{
                  access_token: customTokens.access_token,
                  refresh_token: customTokens.refresh_token,
                }}
              />
            }
          />
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<NotFound />} />
          <Route path="/auth/relay" element={<Relay />} />
          <Route path="/logout/*" element={<Logout />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Fall back to OIDC authentication
  // Show loader during token exchange (e.g., SSO callback with ?code=) to avoid login form flash
  if (auth.isLoading) return <Loader />;
  if (auth.error) return <div>Error: {auth.error.message}</div>;
  if (!auth.isAuthenticated) return <LoginBox />;

  return (
    <BrowserRouter>
      <RemoveAuthCodeFromUrl />
      <Routes>
        <Route
          path="/home"
          element={
            auth.user &&
            typeof auth.user === "object" && (
              <Home
                accessInfo={
                  auth.user as { access_token: string; refresh_token: string }
                }
              />
            )
          }
        />
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="*" element={<NotFound />} />
        <Route path="/sso-test" element={<SsoTest />} />
        <Route path="/auth/relay" element={<Relay />} />
        <Route path="/logout/*" element={<Logout />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
